/**
 * Fenestration Placer Component
 *
 * Tool for placing windows and doors on building surfaces.
 * Provides configuration options for fenestration dimensions and type.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useGeometryEditStore, useFenestrationPlacer } from '../../geometryEditStore';
import { useIDFStore } from '../../../stores/idfStore';
import { useGeometryStore } from '../../geometryStore';
import { useIsMobile } from '../../../hooks';
import type { FenestrationType } from '../../types';
import type { FenestrationPlaceConfig, WindowPreset, DoorPreset } from '../../editingTypes';
import { WINDOW_PRESETS, DOOR_PRESETS } from '../../editingTypes';
import { createFenestrationIDFObject } from '../../sync/fenestrationSync';
import { getSurfaceVertices, getSurfaceName } from '../../sync/surfaceSync';

/**
 * Props for FenestrationPlacer.
 */
interface FenestrationPlacerProps {
  onComplete?: (fenestrationId: string) => void;
  onCancel?: () => void;
}

/**
 * Fenestration Placer Component.
 */
export function FenestrationPlacer({ onComplete, onCancel }: FenestrationPlacerProps) {
  const isMobile = useIsMobile();
  const placer = useFenestrationPlacer();
  const closeFenestrationPlacer = useGeometryEditStore((s) => s.closeFenestrationPlacer);
  const recordAction = useGeometryEditStore((s) => s.recordAction);
  const addNotification = useGeometryEditStore((s) => s.addNotification);

  const addObject = useIDFStore((s) => s.addObject);
  const document = useIDFStore((s) => s.document);
  const geometry = useGeometryStore((s) => s.geometry);

  // Local state
  const [fenType, setFenType] = useState<FenestrationType>('window');
  const [name, setName] = useState('');
  const [width, setWidth] = useState(1.2);
  const [height, setHeight] = useState(1.5);
  const [sillHeight, setSillHeight] = useState(0.9);
  const [horizontalPosition, setHorizontalPosition] = useState(0.5);
  const [construction, setConstruction] = useState('');
  const [applyToSimilar, setApplyToSimilar] = useState(false);

  // Get surface info
  const targetSurface = useMemo(() => {
    if (!geometry || !placer.targetSurfaceId) return null;

    for (const zone of geometry.zones) {
      for (const surface of zone.surfaces) {
        if (surface.id === placer.targetSurfaceId) {
          return { surface, zoneName: zone.name };
        }
      }
    }
    return null;
  }, [geometry, placer.targetSurfaceId]);

  // Get IDF surface object for vertex data
  const targetSurfaceObject = useMemo(() => {
    if (!document || !placer.targetSurfaceId) return null;
    return document.objects.find((obj) => obj.id === placer.targetSurfaceId);
  }, [document, placer.targetSurfaceId]);

  // Calculate surface dimensions
  const surfaceDimensions = useMemo(() => {
    if (!targetSurface) return { width: 0, height: 0 };

    const vertices = targetSurface.surface.vertices;
    if (vertices.length < 4) return { width: 0, height: 0 };

    // For a wall, width is the horizontal distance, height is vertical
    const v0 = vertices[0];
    const v1 = vertices[1];
    const v3 = vertices[3] || vertices[2];
    if (!v0 || !v1 || !v3) return { width: 0, height: 0 };

    const horizontalDist = Math.sqrt(
      Math.pow(v1.x - v0.x, 2) + Math.pow(v1.y - v0.y, 2) + Math.pow(v1.z - v0.z, 2)
    );
    const verticalDist = Math.sqrt(
      Math.pow(v3.x - v0.x, 2) + Math.pow(v3.y - v0.y, 2) + Math.pow(v3.z - v0.z, 2)
    );

    return { width: horizontalDist, height: verticalDist };
  }, [targetSurface]);

  // Get available fenestration constructions
  const availableConstructions = useMemo(() => {
    if (!document) return [];
    return document.objects
      .filter(
        (obj) =>
          obj.className.toLowerCase() === 'construction' ||
          obj.className.toLowerCase() === 'construction:windowdatafile'
      )
      .map((obj) => obj.fields[0]?.value ?? '')
      .filter(Boolean);
  }, [document]);

  // Generate default name
  useEffect(() => {
    if (!targetSurface || name) return;

    const existingFenCount = targetSurface.surface.fenestrations.length;
    const typeLabel = fenType.charAt(0).toUpperCase() + fenType.slice(1);
    setName(`${targetSurface.surface.name} ${typeLabel} ${existingFenCount + 1}`);
  }, [targetSurface, fenType, name]);

  // Apply preset
  const applyPreset = useCallback(
    (preset: WindowPreset | DoorPreset) => {
      setWidth(preset.width);
      setHeight(preset.height);
      if ('sillHeight' in preset) {
        setSillHeight(preset.sillHeight);
      } else {
        setSillHeight(0);
      }
      if (preset.construction) {
        setConstruction(preset.construction);
      }
    },
    []
  );

  const handleClose = useCallback(() => {
    closeFenestrationPlacer();
    onCancel?.();
  }, [closeFenestrationPlacer, onCancel]);

  const handlePlace = useCallback(() => {
    if (!targetSurfaceObject || !targetSurface) {
      addNotification('error', 'No target surface selected');
      return;
    }

    if (!name.trim()) {
      addNotification('error', 'Please enter a fenestration name');
      return;
    }

    // Validate dimensions
    if (width > surfaceDimensions.width) {
      addNotification('error', 'Window width exceeds surface width');
      return;
    }
    if (height + sillHeight > surfaceDimensions.height) {
      addNotification('error', 'Window height + sill height exceeds surface height');
      return;
    }

    const config: FenestrationPlaceConfig = {
      surfaceId: placer.targetSurfaceId!,
      type: fenType,
      name: name.trim(),
      construction: construction || 'Default Window',
      width,
      height,
      sillHeight,
      horizontalPosition,
    };

    // Get parent surface info
    const parentName = getSurfaceName(targetSurfaceObject);
    const parentVertices = getSurfaceVertices(targetSurfaceObject);

    // Create the fenestration IDF object
    const result = createFenestrationIDFObject(config, parentName, parentVertices);

    // Add to IDF
    const fenId = addObject(result.fenestration);

    // Record action for undo
    recordAction({
      type: 'fenestration-create',
      description: `Place ${fenType} "${name}"`,
      data: { config, fenId },
      inverseData: { fenId },
      createdIds: [fenId],
      deletedIds: [],
    });

    addNotification('success', `${fenType.charAt(0).toUpperCase() + fenType.slice(1)} "${name}" placed`);

    // Clear name for next placement
    setName('');

    onComplete?.(fenId);
  }, [
    targetSurfaceObject,
    targetSurface,
    name,
    fenType,
    width,
    height,
    sillHeight,
    horizontalPosition,
    construction,
    surfaceDimensions,
    placer.targetSurfaceId,
    addObject,
    recordAction,
    addNotification,
    onComplete,
  ]);

  if (!placer.isPlacing) return null;

  return (
    <div className={`fixed z-40 rounded-lg bg-popover shadow-xl border max-h-[80vh] overflow-y-auto ${
      isMobile
        ? 'inset-x-2 bottom-20 mb-[env(safe-area-inset-bottom)]'
        : 'bottom-4 right-4 w-80'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3 sticky top-0 bg-popover">
        <h3 className="font-medium text-foreground">Place Fenestration</h3>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Target surface info */}
        {targetSurface && (
          <div className="mb-4 rounded bg-blue-50 px-3 py-2 text-sm">
            <div className="font-medium text-blue-900">{targetSurface.surface.name}</div>
            <div className="text-xs text-blue-700">
              {surfaceDimensions.width.toFixed(1)}m × {surfaceDimensions.height.toFixed(1)}m
            </div>
          </div>
        )}

        {/* Type selection */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
          <div className="flex gap-2">
            <button
              onClick={() => setFenType('window')}
              className={`flex-1 rounded px-3 py-1.5 text-sm ${
                fenType === 'window'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Window
            </button>
            <button
              onClick={() => setFenType('door')}
              className={`flex-1 rounded px-3 py-1.5 text-sm ${
                fenType === 'door'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Door
            </button>
            <button
              onClick={() => setFenType('glassdoor')}
              className={`flex-1 rounded px-3 py-1.5 text-sm ${
                fenType === 'glassdoor'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Glass Door
            </button>
          </div>
        </div>

        {/* Presets */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">Presets</label>
          <div className="flex flex-wrap gap-1">
            {fenType === 'window'
              ? WINDOW_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => applyPreset(preset)}
                    className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200"
                    title={preset.description}
                  >
                    {preset.name}
                  </button>
                ))
              : DOOR_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => applyPreset(preset)}
                    className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200"
                    title={preset.description}
                  >
                    {preset.name}
                  </button>
                ))}
          </div>
        </div>

        {/* Name */}
        <div className="mb-3">
          <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Enter name..."
          />
        </div>

        {/* Dimensions */}
        <div className="mb-3 grid grid-cols-3 gap-2">
          <div>
            <label className="mb-1 block text-xs text-gray-600">Width (m)</label>
            <input
              type="number"
              value={width}
              onChange={(e) => setWidth(parseFloat(e.target.value) || 0)}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              step="0.1"
              min="0.1"
              max={surfaceDimensions.width}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-600">Height (m)</label>
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(parseFloat(e.target.value) || 0)}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              step="0.1"
              min="0.1"
              max={surfaceDimensions.height}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-600">Sill (m)</label>
            <input
              type="number"
              value={sillHeight}
              onChange={(e) => setSillHeight(parseFloat(e.target.value) || 0)}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              step="0.1"
              min="0"
              max={surfaceDimensions.height - height}
            />
          </div>
        </div>

        {/* Horizontal position slider */}
        <div className="mb-3">
          <label className="mb-1 block text-xs text-gray-600">
            Horizontal Position: {(horizontalPosition * 100).toFixed(0)}%
          </label>
          <input
            type="range"
            value={horizontalPosition}
            onChange={(e) => setHorizontalPosition(parseFloat(e.target.value))}
            className="w-full"
            min="0"
            max="1"
            step="0.01"
          />
          <div className="mt-1 flex justify-between text-xs text-gray-400">
            <span>Left</span>
            <span>Center</span>
            <span>Right</span>
          </div>
        </div>

        {/* Construction */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">Construction</label>
          <select
            value={construction}
            onChange={(e) => setConstruction(e.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">Select construction...</option>
            {availableConstructions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Apply to similar */}
        <label className="mb-4 flex items-center gap-2">
          <input
            type="checkbox"
            checked={applyToSimilar}
            onChange={(e) => setApplyToSimilar(e.target.checked)}
            className="h-4 w-4 rounded text-blue-600"
          />
          <span className="text-sm text-gray-700">Apply to all similar surfaces</span>
        </label>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 border-t bg-gray-50 px-4 py-3">
        <button
          onClick={handleClose}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handlePlace}
          disabled={!name.trim() || !targetSurface}
          className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
        >
          Place {fenType === 'window' ? 'Window' : fenType === 'door' ? 'Door' : 'Glass Door'}
        </button>
      </div>
    </div>
  );
}

export default FenestrationPlacer;
